import { useSearchParams } from 'react-router-dom';
import {
  Tabs as TabsUI,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../ui/tabs';
import {
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../ui/hover-card';
import { Check, ChevronDown } from 'lucide-react';
import {
  MultipleTriggerProps,
  SingleTriggerProps,
} from '@/components/molecule/Tabs/types';

// Context
interface TabsContextProps {
  selectedValue: string;
  setSelectedValue: (value: string) => void;
}
const TabsContext = createContext<TabsContextProps>({
  selectedValue: '',
  setSelectedValue: () => {},
});

interface TabsContentContextProps {
  value: string;
}
const TabsContentContext = createContext<TabsContentContextProps>({
  value: '',
});

interface TabsProps {
  children: React.ReactNode;
  hasSearchParams?: boolean;
  defaultValue?: string;
  onChange?: (value: string) => void;
  includeValues?: string[];
}

const checkIncludeValues = (
  value?: string | null,
  includeValues?: string[],
) => {
  if (!value || !includeValues) return value;

  const newValue = includeValues.find(
    item => item.includes(value) || value.includes(item),
  );

  return newValue || includeValues[0];
};

export default function Tabs({
  defaultValue,
  children,
  onChange,
  hasSearchParams = false,
  includeValues,
}: TabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedValue, setSelectedValue] = useState(
    (hasSearchParams &&
      checkIncludeValues(searchParams.get('tabValue'), includeValues)) ||
      defaultValue ||
      '',
  );

  useEffect(() => {
    if (hasSearchParams && selectedValue) {
      searchParams.set('tabValue', selectedValue);

      setSearchParams(searchParams, { replace: true });
    }
  }, [hasSearchParams, searchParams, selectedValue, setSearchParams]);

  return (
    <TabsContext.Provider
      value={{
        selectedValue,
        setSelectedValue: value => {
          if (value) {
            setSelectedValue(value);
            onChange?.(value);
          }
        },
      }}
    >
      <TabsUI
        defaultValue={selectedValue.split('-')?.[0] || ''}
        onValueChange={e => {
          if (e) {
            setSelectedValue(e);
            onChange?.(e);
          }
        }}
      >
        {children}
      </TabsUI>
    </TabsContext.Provider>
  );
}

Tabs.TabsList = memo(({ children }: { children: React.ReactNode }) => {
  return (
    <TabsList className="none-scrollbar flex justify-start overflow-x-auto overflow-y-hidden">
      {children}
    </TabsList>
  );
});
Tabs.TabsContents = memo(TabsContent);

Tabs.SingleTrigger = memo(
  ({ value, label, defaultChecked }: SingleTriggerProps) => {
    return (
      <TabsTrigger
        key={value}
        className="w-[160px] flex-shrink-0"
        value={value}
        defaultChecked={defaultChecked}
      >
        {label}
      </TabsTrigger>
    );
  },
);

const TabMultipleTrigger = ({
  value,
  label,
  optionTrigger,
}: MultipleTriggerProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { selectedValue, setSelectedValue } = useContext(TabsContext);

  const optionValues = useMemo(
    () => optionTrigger?.map(e => e.value) || [],
    [optionTrigger],
  );

  const isSelected = useMemo(
    () => [value, ...optionValues].includes(selectedValue),
    [optionValues, selectedValue, value],
  );

  // optionTrigger에 defaultOption 값이 있는 경우 defaultOption을 선택하고, 없는 경우 value를 선택
  const defaultValue = useMemo(() => {
    return optionTrigger?.find(e => e.isDefault)?.value || value;
  }, [optionTrigger, value]);

  const [hoverOpen, setHoverOpen] = useState(false);
  const handleOpen = (isOpen: boolean) => {
    setHoverOpen(isOpen);
  };

  useEffect(() => {
    setHoverOpen(false);
  }, [selectedValue]);

  return (
    <HoverCard
      key={value}
      openDelay={0}
      closeDelay={200}
      onOpenChange={handleOpen}
      open={hoverOpen}
    >
      <TabsTrigger
        className="h-[40px] min-w-[170px] flex-shrink-0 p-0 [&>div>div]:translate-y-[2px]"
        value={defaultValue}
        isSelected={isSelected}
      >
        <HoverCardTrigger className="flex w-full items-center justify-center gap-2">
          {label} <ChevronDown size={16} />
        </HoverCardTrigger>
        <HoverCardContent className="w-fit min-w-[170px] p-0">
          <ul className="flex w-full flex-col items-start p-1">
            {optionTrigger &&
              optionTrigger.map(option => {
                return (
                  <TabMultipleTriggerChild
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                );
              })}
          </ul>
        </HoverCardContent>
      </TabsTrigger>
    </HoverCard>
  );
};
Tabs.MultipleTrigger = memo(TabMultipleTrigger);

const TabMultipleTriggerChild = memo(({ value, label }: SingleTriggerProps) => {
  const { selectedValue, setSelectedValue } = useContext(TabsContext);

  return (
    <li
      className="flex w-full items-center gap-2 p-2 text-b14r"
      onClick={e => {
        e.stopPropagation();
        e.nativeEvent.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setSelectedValue(value);
      }}
    >
      <Check
        style={{
          display: selectedValue === value ? 'flex' : 'none',
        }}
        size={16}
        className="flex-shrink-0"
      />
      {label}
    </li>
  );
});

interface TabListContentProps {
  value: string;
  children: React.ReactNode;
}

const TabListSingleContent = ({ children, value }: TabListContentProps) => {
  return <TabsContent value={value}>{children}</TabsContent>;
};
Tabs.SingleContent = memo(TabListSingleContent);

const TabListMultipleContent = ({ children, value }: TabListContentProps) => {
  return (
    <TabsContentContext.Provider value={{ value }}>
      <TabsContent value={value}>{children}</TabsContent>
    </TabsContentContext.Provider>
  );
};
Tabs.MultipleContent = memo(TabListMultipleContent);

const TabListMultipleContentChild = ({
  children,
  value,
}: TabListContentProps) => {
  const { value: parentValue } = useContext(TabsContentContext);
  const { selectedValue } = useContext(TabsContext);
  const [isFirst, setIsFirst] = useState(false);

  return (
    <div
      ref={ref => {
        setIsFirst(!ref?.previousSibling);
      }}
    >
      {(selectedValue === value ||
        (selectedValue === parentValue && isFirst)) &&
        children}
    </div>
  );
};
Tabs.MultipleContentChild = memo(TabListMultipleContentChild);
